import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import AptiosITalic from "../../../../utils/Customfonts/ArialCEItalic.ttf";
import { HSE_CHECK_POINTS, OPEN_CLOSE_OPTIONS } from "../../GeneralIR/generalIRConst";
import { HSE_OPTIONS_LABEL } from "../../GeneralIR/hseCheckPoints";
import { GEN_TextField_Types, INS_ACT_Checkbox_Types } from "../../GeneralIR/generalIRConst";
import { formateDate } from "../../../../utils/commonComponent/commonConstant";
import { getSelectLabel } from "./ViewIRReport";

// ASSET PATHS
const UnitechHeader = "../img/IR_Header.png";
const UnitechFooter = "../img/IR_Footer.png";

// FONT FAMILY  –  declared first so every helper
export const customFontFamily = {
  bold:       "bold",
  normal:     "normal",
  boldFont:   "AptosBold",
  normalFont: "AptosNormal",
  italic:     "italic",
  italicFont: "AptiosITalic",
};

// CONFIG  –  tweak any value here; everything
export const config = {
  // Page margins
  margin:       25,   // left / right content margin
  borderMargin: 15,   // rect border inset from page edge
  // Header / footer image heights (set 0 to hide)
  headerimgHeight: 70,
  footerimgHeight: 0,
  // Page-number strip height (below footer)
  pageNoHeight: 15,

  // Gap between header image bottom and content rect top
  borderGap: 2,

  // Default font sizes
  fontSize:      9,
  fontSizeSm:    8,
  fontSizeLg:    11,
  fontSizeTitle: 14,

  // Row / cell padding inside autoTable
  cellPadding: 4,

  // Spacing added after every section (pt)
  sectionGap: 10,

  // Spacing for bottom border and bottom content between
  borderFooterGap: 15,

  // Inspection report title
  inspectionHeading: false,

  // Disclaimer footer text (set showDisclaimer: false to hide)
  showDisclaimer: true,
  disclaimerHeight: 45,
  disclaimerText: "Disclaimer – This inspection report doesn't relieve the Client/Supplier/Manufacturer/Stockiest from responsibility to supply genuine materials. DYCGPL's responsibility is limited to inspection accuracy, not material authenticity. Calibration certificates without traceability limit the review to technical content only. The report represents findings solely within the scope and parameters agreed upon (DYC General terms & Conditions) and may not cover all aspects or potential issues. DYC Global Pvt. Ltd are not liable for any matters beyond the agreed inspection scope. Recipients are advised to independently verify and make decisions based on the report. This report serves as a record of the inspection visit and does not imply warranties or guarantees.\nCopyright: This document is owned by DYC Global Pvt. Ltd. Reproduction, in whole or in part, is prohibited without the consent of DYC Global Pvt. Ltd.\nDYC Global Pvt. Ltd. (Regd. & HO) : 7th floor, B-702, Lodha Supremus, Kolshet Road, Thane West 400607, Maharashtra, India.\nContact: +91 2249628259/ +91 8591929410 ; E - Mail: inspection@dycgpl.com; Website: www.dycgpl.com",

  // Computed helpers (derived – do not set manually)
  get contentTop() {
    return this.headerimgHeight + this.borderGap + this.borderMargin + 15;
  },
  get contentBottom() {
  const disclaimerSpace = this.showDisclaimer ? this.disclaimerHeight : 0;
  return this.footerimgHeight + this.pageNoHeight + this.borderGap + this.borderMargin + 5 + disclaimerSpace;
  },
};

// SHARED STYLE OBJECTS  (use Aptos everywhere)
const getBaseStyles = () => ({
  fontSize:    config.fontSize,
  cellPadding: config.cellPadding,
  lineColor:   [0, 0, 0],
  lineWidth:   0.5,
  textColor:   [0, 0, 0],
  fillColor:   false,
  font:        customFontFamily.normalFont,  // AptosNormal
  fontStyle:   customFontFamily.normal,      // "normal"
  halign:      "left",
  valign:      "top",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font:      customFontFamily.boldFont,  // AptosBold
  fontStyle: customFontFamily.bold,      // "bold"
  fillColor: [240, 244, 247],
  valign:    "middle",
});

const getTableMargins = () => ({
  left:   config.margin,
  right:  config.margin,
  top:    config.contentTop,    // keeps rows below header image on new pages
  bottom: config.contentBottom, // keeps rows above footer image
});

// LAYOUT STATE  (shared cursor across sections)
const layout = {
  doc:          null,
  y:            0,
  pageWidth:    0,
  pageHeight:   0,
  contentWidth: 0,
  reportNo:     "",

  init(doc, reportNo = "") {
    this.doc          = doc;
    this.reportNo     = reportNo;
    this.pageWidth    = doc.internal.pageSize.getWidth();
    this.pageHeight   = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y            = config.contentTop;
  },

  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },

  advance(delta) {
    this.y += delta;
  },

  ensureSpace(needed = 30) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

// FONT LOADER
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes     = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  const fontResponse = await fetch(fontUrl);
  const fontBuffer   = await fontResponse.arrayBuffer();
  const fontBase64   = arrayBufferToBase64(fontBuffer);
  pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
  pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
};

// PAGE DECORATORS
const addHeaderFooter = (doc) => {
  const marginX = 0;
  const width   = layout.pageWidth;

  if (config.headerimgHeight > 0) {
    doc.addImage(UnitechHeader, "PNG", marginX, 0, width, config.headerimgHeight);
  }
  if (config.footerimgHeight > 0) {
    doc.addImage(
      UnitechHeader,
      "PNG",
      marginX,
      layout.pageHeight - config.footerimgHeight - 3,
      width,
      config.footerimgHeight
    );
  }

  // FIX 2 – Title and report-number are pushed down so they sit
  // clearly below the header image and don't overlap it.
  // Old:  titleY = headerimgHeight + borderGap + borderMargin - 2
  //       title drawn at titleY - 10  →  overlapped the header image
  // New:  titleY = headerimgHeight + borderGap + borderMargin + 8
  //       title drawn at titleY - 4   →  sits cleanly below header
  const titleY = config.headerimgHeight + config.borderGap + config.borderMargin - 5;

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  if(config.inspectionHeading) {
    doc.setFontSize(config.fontSizeTitle);
    doc.setTextColor(0);
    doc.text("INSPECTION REPORT", layout.pageWidth / 2, titleY, { align: "center" });
  }

  doc.setFontSize(config.fontSize);
  doc.text(`Report Number – ${layout.reportNo || "—"}`, config.borderMargin, titleY);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
};

const drawMarginRect = (doc) => {
  const topSpace    = config.headerimgHeight + config.borderGap;
  // bottom of rect stops ABOVE the disclaimer area
  const disclaimerSpace = config.showDisclaimer ? config.disclaimerHeight : 0;
  const bottomSpace = config.footerimgHeight + config.pageNoHeight + config.borderGap + disclaimerSpace;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(
    config.borderMargin,
    config.borderMargin + topSpace,
    layout.pageWidth  - config.borderMargin * 2,
    layout.pageHeight - config.borderMargin * 2 - topSpace - bottomSpace
  );
};

const addPageNumbers = (doc, totalPages) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addHeaderFooter(doc);
    drawMarginRect(doc);

    const pageNoY = layout.pageHeight - config.footerimgHeight - config.borderFooterGap;

    // Disclaimer — sits between border bottom and page number, no border
    if (config.showDisclaimer) {
      const maxWidth = layout.pageWidth - config.borderMargin * 2;
      const lineHeight = 6;
      const disclaimerStartY = pageNoY - config.disclaimerHeight - 4;
      let curY = disclaimerStartY;

      doc.setFontSize(5.5);
      doc.setTextColor(0);
      const drawLabelPlusText = (label, text) => {
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        const labelW = doc.getTextWidth(label);
        const fullText = label + text;
        const allLines = doc.splitTextToSize(fullText, maxWidth);
        allLines.forEach((line, idx) => {
          if (idx === 0) {
            doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
            doc.text(label, config.borderMargin, curY);
            doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
            const textPart = line.slice(label.length);
            if (textPart) doc.text(textPart, config.borderMargin + labelW, curY);
          } else {
            doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
            doc.text(line, config.borderMargin, curY);
          }
          curY += lineHeight;
        });
      };

      drawLabelPlusText(
        "Disclaimer – ",
        "This inspection report doesn't relieve the Client/Supplier/Manufacturer/Stockiest from responsibility to supply genuine materials. DYCGPL's responsibility is limited to inspection accuracy, not material authenticity. Calibration certificates without traceability limit the review to technical content only. The report represents findings solely within the scope and parameters agreed upon (DYC General terms & Conditions) and may not cover all aspects or potential issues. DYC Global Pvt. Ltd are not liable for any matters beyond the agreed inspection scope. Recipients are advised to independently verify and make decisions based on the report. This report serves as a record of the inspection visit and does not imply warranties or guarantees."
      );

      drawLabelPlusText(
        "Copyright:",
        " This document is owned by DYC Global Pvt. Ltd. Reproduction, in whole or in part, is prohibited without the consent of DYC Global Pvt. Ltd."
      );

      drawLabelPlusText(
        "DYC Global Pvt. Ltd. (Regd. & HO) :",
        " 7th floor, B-702, Lodha Supremus, Kolshet Road, Thane West 400607, Maharashtra, India."
      );

      // Contact line — inline bold+normal segments
      let x = config.borderMargin;
      const contactSegments = [
        { text: "Contact:", bold: true },
        { text: " +91 2249628259/ +91 8591929410 ; ", bold: false },
        { text: "E – Mail:", bold: true },
        { text: " inspection@dycgpl.com; ", bold: false },
        { text: "Website:", bold: true },
        { text: " www.dycgpl.com", bold: false },
      ];
      contactSegments.forEach((seg) => {
        doc.setFont(
          seg.bold ? customFontFamily.boldFont : customFontFamily.normalFont,
          seg.bold ? customFontFamily.bold : customFontFamily.normal
        );
        doc.setFontSize(5.5);
        doc.text(seg.text, x, curY);
        x += doc.getTextWidth(seg.text);
      });
    }
    // Page number line
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSizeSm);
    doc.setTextColor(0);
    doc.text("DYC/FMT-INSP/01 Rev.00 ; Date-24.05.2023", config.borderMargin, pageNoY);
    doc.text(
      `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`,
      layout.pageWidth - config.margin - 45,
      pageNoY
    );
  }
};

// HELPER – gray section heading bar
const addSectionHeading = (number, title, note = "") => {
  // FIX 3 – Guard against the heading starting so close to the bottom
  // of a page that autoTable triggers an internal page-break mid-render,
  // which left the heading cut off or overlapping the border rect.
  // ensureSpace(40) forces a clean new page before drawing if needed.  layout.ensureSpace(60);
  // Build cell content with note on new line in smaller italic
  const label = `${number}. ${title}`;
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   [[label]],
    theme:  "grid",
    styles: {
      ...getBaseStyles(),
      font:        customFontFamily.boldFont,
      fontStyle:   customFontFamily.bold,
      fontSize:    config.fontSize,
      fillColor:   [240, 244, 247],
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
    didDrawCell: (data) => {
      if (note && data.row.index === 0 && data.column.index === 0) {
        const noteX = data.cell.x + 4 + layout.doc.getTextWidth(label) + 2;
        const noteY = data.cell.y + data.cell.height / 2 + config.fontSize * 0.3;
        layout.doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
        layout.doc.setFontSize(config.fontSize - 1);
        layout.doc.setTextColor(0);
        layout.doc.text(`(${note})`, noteX, noteY);
        // reset font
        layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        layout.doc.setFontSize(config.fontSize);
      }
    },
  });
  layout.afterTable(0);
};

// HELPER – standard multi-column autoTable
const addDataTable = ({ head, body, columnStyles }) => {
  autoTable(layout.doc, {
    startY:     layout.y,
    margin:     getTableMargins(),
    head,
    body,
    theme:      "grid",
    headStyles: getHeaderStyles(),
    styles:     getBaseStyles(),
    columnStyles,
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// HELPER – plain text block (free-text sections)
const addTextBlock = (text, color = [0, 0, 0]) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   [[text || ""]],
    theme:  "grid",
    styles: { ...getBaseStyles(), minCellHeight: 20, textColor: color },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage:  () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// SECTION 1 & 2 – General Information + Type of Inspection
export const addSection1And2 = (inputStates) => {
  const colW   = layout.contentWidth;
  const leftW  = colW * 0.30;
  const midW   = colW * 0.35;
  const rightW = colW * 0.35;

  const head = [[
    { content: "1. General Information",  colSpan: 2, styles: getHeaderStyles() },
    {
      content: "2. Type of Inspection Activity:",
      styles:  { ...getHeaderStyles(), minCellHeight:28 },
    },
  ]];

  const body = GEN_TextField_Types.map((field, i) => {
    const checkbox  = INS_ACT_Checkbox_Types[i];
    const checked   = checkbox && inputStates[checkbox.key] == 1;
    const checkCell = checkbox
      ? `${checked ? "☑" : "☐"}  ${checkbox.label}`
      : "";

    return [
      {
        content: field.label + (field.subLabel ? `\n${field.subLabel}` : ""),
        styles:  { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      },
      { content: field.isDate ? formateDate(inputStates[field?.key]) : (inputStates[field?.key] || ""), styles: getBaseStyles() },
      { 
        content: checkCell,
        styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      },
    ];
  });

  autoTable(layout.doc, {
    startY:     layout.y,
    margin:     getTableMargins(),
    head,
    body,
    theme:      "grid",
    headStyles: getHeaderStyles(),
    styles:     getBaseStyles(),
    columnStyles: {
      0: { cellWidth: leftW },
      1: { cellWidth: midW },
      2: { cellWidth: rightW },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
    didDrawCell: (data) => {
      if (data.section === "head" && data.column.index == 2 && data.row.index == 0) {
        const noteText = "(Select the Applicable Stage)";
        const cellX = data.cell.x + 4;
        const cellY = data.cell.y + data.cell.height / 2 + config.fontSize * 0.8;
        layout.doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
        layout.doc.setFontSize(config.fontSize - 1);
        layout.doc.setTextColor(0);
        layout.doc.text(noteText, cellX, cellY + 4);
        // reset
        layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        layout.doc.setFontSize(config.fontSize);
      }
    },
  });
  layout.afterTable();
};

// SECTION 3 – Inspected Items
const addSection3 = (itemDetails) => {
  addSectionHeading(3, "Details of Inspected Items", "Fill the data as per PO/Offer list");

  const head = [[
    "PO Item No", "Item Code",
    "Item Description / Identification Number / MTC Number / Manufacturer",
    "P.O. Quantity with UOM", "Offered Quantity with UOM", "Acceptable Quantity with UOM",
  ]];
  const body = itemDetails.length
    ? itemDetails.map((r) => [
        r.poItemNo || "", r.itemCode || "", r.itemDescription || "",
        r.poQuantityWithUom || "", r.offeredQuantityWithUom || "", r.acceptableQuantityWithUom || "",
      ])
    : [["—", "", "", "", "", ""]];

  addDataTable({ head, body, columnStyles: { 0: { cellWidth: 50 }} });
};

// SECTION 4 – Reference Documents
const addSection4 = (applicableDocRows) => {
  addSectionHeading(4, "Applicable Reference Documents:", "Fill applicable section or mention not applicable");

  const head = [["Sr.No.", "Document Name", "Client Document Number", "Vendor Document Number", "Approval Status"]];
  const body = applicableDocRows.length
    ? applicableDocRows.map((r, i) => [
        i + 1, r.documentName || "", r.clientDocumentNumber || "", r.vendorDocumentNumber || "", r.approvalStatus || "",
      ])
    : [["—", "", "", "", ""]];

  addDataTable({ head, body, columnStyles: { 0: { cellWidth: 50 } } });
};

// SECTION 5 – Area of Concern
const addSection5 = (areaOfConcernRows) => {
  addSectionHeading(5, "Area of Concern", "From This Visit & Previous visit, do not delete the concerns after closing");

  const head = [["Sr. No.", "Area of Concern / Observations / Non-Conformities", "Date Initiated", "Open / Close", "Action By"]];
  const body = areaOfConcernRows.length
    ? areaOfConcernRows.map((r, i) => [
        i + 1, r.areaOfConcern || "", formateDate(r.dateInitiated || ""), getSelectLabel(OPEN_CLOSE_OPTIONS, r.openClose), r.actionBy || "",
      ])
    : [["—", "", "", "", ""]];

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: 50 } },
    didParseCell: (data) => {
      if (data.section === "body" && areaOfConcernRows[data.row.index]?.openClose === 2) {
        data.cell.styles.textColor = [220, 38, 38];
      }
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// SECTION 6 – Inspection Stages Witnessed
const addSection6 = (detailsWitnessRows) => {
  addSectionHeading(6, "Details of Inspection stages witnessed:", "Mention Witnessed activities as per scope – Findings of Inspection");

  const head = [["QAP Stage", "Activities description as Per QAP"]];
  const body = detailsWitnessRows.length
    ? detailsWitnessRows.map((r) => [r.qapStage || "", r.activitiesDescription || ""])
    : [["—", ""]];

  addDataTable({ head, body, columnStyles: { 0: { cellWidth: 50 } } });
};

// SECTION 7 – Reviewed Documents
const addSection7 = (detailsReviewedRows) => {
  addSectionHeading(7, "Details of Reviewed Documents:", "Mention reviewed documents as per scope with findings");

  const head = [["QAP Stage", "Activities description as Per QAP"]];
  const body = detailsReviewedRows.length
    ? detailsReviewedRows.map((r) => [r.qapStage || "", r.reviewedactivitiesDescription || ""])
    : [["—", ""]];

  addDataTable({ head, body, columnStyles: { 0: { cellWidth: 50 } } });
};

// SECTION 8 – Result Summary
const addSection8 = (inputStates) => {
  addSectionHeading(8, "Result Summary:", "Mention Conclusive Result of Inspection here");
  addTextBlock(inputStates.resultSummary);
};

// SECTION 9 – Equipment Used
const addSection9 = (detailsEquipmentRows) => {
  addSectionHeading(9, "Details of Equipment(s) / Instrument(s) used for Measuring / Testing during Inspection:");

  const head = [[
    "Sr. No.", "Name of Equipment", "Range", "ID No.",
    "Calibration Due Date", "Calibration Certificate Number",
    "Traceability to NABL / ISO 17025 accredited lab (Yes/No)",
  ]];
  const body = detailsEquipmentRows.length
    ? detailsEquipmentRows.map((r, i) => [
        i + 1, r.nameOfEquipment || "", r.ranges || "", r.idNo || "",
        formateDate(r.calibrationDueDate || ""), r.calibrationCertificateNumber || "", r.traceability || "",
      ])
    : [["—", "", "", "", "", "", ""]];
  
  body.push([
    {
      content: "All above measuring instruments and equipment underwent a verification process to confirm their ongoing suitability for intended use, accurate identification, proper calibration status, and compliance with national standards, all of which were determined to be satisfactory.",
      colSpan: 7,
      styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, fontStyle: customFontFamily.normal },
    },
  ]);

  addDataTable({ head, body, columnStyles: { 0: { cellWidth: 50 } } });
};

// SECTION 10 – Pending Activities
const addSection10 = (inputStates) => {
  addSectionHeading(10, "Pending Activities:", "Write pending activities from this visit against offered scope / RFI");
  addTextBlock(inputStates.pendingActivities, [255, 0, 0]); // red color apply
};

// SECTION 11 – Identification of Inspected Items
const addSection11 = (inputStates) => {
  addSectionHeading(11, "Identification of the Inspected Items:", "Mention identification details where stamping has been done");
  addTextBlock(inputStates.identificationOfItems);
};

// SECTION 12 – Non-Conformity Report
const addSection12 = (inputStates) => {
  addSectionHeading(12, "Non-Conformity Report:", "Strike through options that are not applicable");
  addTextBlock(inputStates.nonConformity);
};

// SECTION 13 – List of Attachments
const addSection13 = (inputStates) => {
  addSectionHeading(13, "List of Attachments:", "Write short description of supporting documents to inspection");
  addTextBlock(inputStates.listOfAttachments);
};

// SECTION 14 – HSE Check Points
const addSection14 = (hseData) => {
  addSectionHeading(14, "HSE check points:", "Write Yes / No / NA as applicable");

  const HSE_LABEL = HSE_OPTIONS_LABEL.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  const hseLeft  = HSE_CHECK_POINTS.filter((_, i) => i % 2 === 0);
  const hseRight = HSE_CHECK_POINTS.filter((_, i) => i % 2 !== 0);

  const colW   = layout.contentWidth;
  const labelW = colW * 0.38;
  const valW   = colW * 0.12;

  const body = hseLeft.map((lp, i) => {
    const rp   = hseRight[i];
    const lVal = hseData[lp.key] == 0 ? "-" : (HSE_LABEL[hseData[lp.key]] ?? "—");
    const rVal = rp
      ? hseData[rp.key] == 0 ? "-" : (HSE_LABEL[hseData[rp.key]] ?? "—")
      : "";

    return [
      { content: lp.label,       styles: getBaseStyles() },
      { content: lVal,            styles: { ...getBaseStyles(), halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: rp?.label || "", styles: getBaseStyles() },
      { content: rVal,            styles: { ...getBaseStyles(), halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ];
  });
  body.push([
    {
      content:
        "Safety requirements are evaluated by referencing Work Instruction DYC/INSP-WI/68 both before initiating and during the execution of inspection activities to ensure safety of working environment.",
      colSpan: 4,
      styles: {...getBaseStyles(), fontSize: config.fontSizeSm, fontStyle: customFontFamily.normal, halign: "left"},
    },
  ]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body,
    theme:  "grid",
    styles: getBaseStyles(),
    columnStyles: {
      0: { cellWidth: labelW },
      1: { cellWidth: valW },
      2: { cellWidth: labelW },
      3: { cellWidth: valW },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(0);
};

// SECTION 15 – Authorized Signatures
const addSection15 = (inputStates) => {
  layout.ensureSpace(130);
  addSectionHeading(15, "Authorized Signatures:");

  const colW = layout.contentWidth / 2;
  const nameColW = layout.contentWidth * 0.25;
  const dateColW = layout.contentWidth * 0.25;
  const sigRowHeight = 80;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        {
          content: "DYC Global Pvt. Ltd. Surveyor:",
          colSpan: 2,
          styles: { ...getHeaderStyles(), halign: "center", valign: "middle" }
        },
        {
          content: "DYC Global Pvt. Ltd. Reviewer:",
          colSpan: 2,
          styles: { ...getHeaderStyles(), halign: "center", valign: "middle" }
        }
      ],
      [
        {
          content: "",
          colSpan: 2,
          styles: { minCellHeight: sigRowHeight, valign: "middle" }
        },
        {
          content: "",
          colSpan: 2,
          styles: { minCellHeight: sigRowHeight, valign: "middle" }
        }
      ],
      [
        {
          content: `Name: ${inputStates.SurveyorName || ""}`,
          styles: { halign: "left", valign: "middle", fontStyle: customFontFamily.bold, font: customFontFamily.boldFont }
        },
        {
          content: `Date: ${formateDate(inputStates.SurveyorDate)}`,
          styles: { halign: "left", valign: "middle" }
        },
        {
          content: `Name: ${inputStates.ReviewerName || ""}`,
          styles: { halign: "left", valign: "middle", fontStyle: customFontFamily.bold, font: customFontFamily.boldFont }
        },
        {
          content: `Date: ${formateDate(inputStates.ReviewerDate)}`,
          styles: { halign: "left", valign: "middle" }
        }
      ]
    ],
    theme: "grid",
    styles: {
      ...getBaseStyles(),
      lineColor: [0, 0, 0],
      lineWidth: 0.5
    },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    columnStyles: {
      0: { cellWidth: nameColW },
      1: { cellWidth: dateColW },
      2: { cellWidth: nameColW },
      3: { cellWidth: dateColW }
    },
    didDrawCell: (data) => {
      // Signature row
      if (data.row.index === 1) {
        const { x, y, width: w, height: h } = data.cell;

        const imgW = 100;
        const imgH = 80;
        const imgX = x + (w - imgW) / 2;
        const imgY = y + (h - imgH) / 2;

        // Surveyor Signature
        if (data.column.index === 0 && inputStates.Surveyorsign) {
          layout.doc.addImage(
            inputStates.Surveyorsign,
            "PNG",
            imgX,
            imgY,
            imgW,
            imgH
          );
        }

        // Reviewer Signature
        if (data.column.index === 1 && inputStates.ReviewerSign) {
          layout.doc.addImage(
            inputStates.ReviewerSign,
            "PNG",
            imgX,
            imgY,
            imgW,
            imgH
          );
        }
      }
    },

    didDrawPage: () => {
      layout.y = config.contentTop;
    }
  });

  layout.afterTable();
};

// SECTION 16 – Evaluation Photographs
const addSection16 = async (uploadedImages) => {
  if (!uploadedImages.length) return;

  layout.ensureSpace(150);
  addSectionHeading(16, "Evaluation Photographs:");

  const photoW   = (layout.contentWidth - 10) / 2;
  const photoH   = 130;
  const captionH = 16;
  const padding  = 4;

  for (let i = 0; i < uploadedImages.length; i += 2) {
    const left  = uploadedImages[i];
    const right = uploadedImages[i + 1];
    const rowH  = photoH + captionH + padding * 2;

    layout.ensureSpace(rowH + 10);
    const rowY = layout.y;

    const drawPhoto = (img, xOffset) => {
      const x = config.margin + xOffset;
      layout.doc.setDrawColor(0);
      layout.doc.setLineWidth(0.3);
      layout.doc.rect(x, rowY, photoW, photoH + captionH);

      try {
        layout.doc.addImage(img.imagePath, "JPEG",
          x + padding, rowY + padding, photoW - padding * 2, photoH - padding * 2);
      } catch (_) { /* skip broken images */ }

      if (img.imagesName) {
      // Divider line between image and caption
        layout.doc.setDrawColor(0);
        layout.doc.setLineWidth(0.3);
        layout.doc.line( x, rowY + photoH, x + photoW, rowY + photoH);
        // Caption
        layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
        layout.doc.setFontSize(config.fontSizeSm);
        layout.doc.text(img.imagesName, x + photoW / 2, rowY + photoH + captionH / 2 + 2, { align: "center" });
      }
    };

    drawPhoto(left, 0);
    if (right) drawPhoto(right, photoW + 10);

    layout.advance(rowH);
  }
};

// MAIN EXPORT
export async function generateIRReportPDF(
  inputStates,
  setDownloadPdfloading,
  itemDetails          = [],
  applicableDocRows    = [],
  areaOfConcernRows    = [],
  detailsWitnessRows   = [],
  detailsReviewedRows  = [],
  detailsEquipmentRows = [],
  hseData              = {},
  uploadedImages       = [],
) {
  try {
    setDownloadPdfloading(true);

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    // Load both Aptos variants in parallel, then set normal as the default
    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos",      customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold,   "Aptos-Bold", customFontFamily.boldFont,   customFontFamily.bold),
      loadAndAddFont(doc, AptiosITalic, "Aptos-Italic", customFontFamily.italicFont, customFontFamily.italic),
    ]);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    // Initialise shared layout state
    layout.init(doc, inputStates.reportNo);

    // ── SECTIONS ──────────────────────────────
    addSection1And2(inputStates);
    addSection3(itemDetails);
    addSection4(applicableDocRows);
    addSection5(areaOfConcernRows);
    addSection6(detailsWitnessRows);
    addSection7(detailsReviewedRows);
    addSection8(inputStates);
    addSection9(detailsEquipmentRows);
    addSection10(inputStates);
    addSection11(inputStates);
    addSection12(inputStates);
    addSection13(inputStates);
    addSection14(hseData);
    addSection15(inputStates);
    await addSection16(uploadedImages);

    // ── PAGE DECORATION (header / footer / border / page numbers) ──
    addPageNumbers(doc, doc.getNumberOfPages());

    doc.save(`Inspection_Report_${inputStates.reportNo || ""}.pdf`);
  } catch (error) {
    console.error("PDF generation error:", error);
  } finally {
    setDownloadPdfloading(false);
  }
}